import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPage.css";

/** Privacy Policy — plain-English starting template (lawyer review recommended before scale). */

const PrivacyPage: React.FC = () => (
  <div className="pp-page">
    <div className="pp-wrap">
      <Link to="/" className="pp-back">← Back to Everything Eco</Link>
      <span className="pp-eyebrow">🌿 Your privacy matters</span>
      <h1>Privacy Policy</h1>
      <p className="pp-updated">Last updated: July 7, 2026</p>

      <div className="pp-card">
        <p>
          Everything Eco NYC ("Everything Eco," "we," "us," or "our") helps New Yorkers recycle, donate,
          and give a second life to their stuff. We care about people first — and that includes protecting
          your personal information. This policy explains what we collect, why, and the choices you have.
        </p>

        <h2>1. Information we collect</h2>
        <p>We only collect what we need to schedule your pickup and keep you updated:</p>
        <ul>
          <li><strong>Contact details</strong> — your name, email address, and phone number.</li>
          <li><strong>Pickup information</strong> — your pickup address, the type of items you're donating, your preferred date and time, and any photos you choose to upload.</li>
          <li><strong>Messages</strong> — anything you send us through our forms, chat, or email.</li>
          <li><strong>Basic usage data</strong> — standard information your browser shares (like device type and pages visited) that helps us keep the site running and improve it.</li>
        </ul>

        <h2>2. How we use your information</h2>
        <ul>
          <li>To schedule, confirm, and complete your free pickup.</li>
          <li>To send you confirmations, reminders, and updates about your pickup.</li>
          <li>To respond to your questions and provide support.</li>
          <li>To send impact updates, recycling tips, and news — but only if you've asked to receive them.</li>
          <li>To measure our community impact and improve our services.</li>
        </ul>

        <h2>3. Your data on devices we recycle</h2>
        <p>
          If you hand off a computer, phone, or other data-bearing device, we can wipe or physically destroy
          its storage so your personal or business data is gone for good. Just let us know when you book.
          Data destruction is handled through our recycling process and is separate from the information you
          share on this website.
        </p>

        <h2>4. How we share information</h2>
        <p>We do <strong>not</strong> sell your personal information. We share it only in limited cases:</p>
        <ul>
          <li><strong>Nonprofit partners</strong> — when you request a tax-deductible donation receipt, we share the details needed to issue it.</li>
          <li><strong>Service providers</strong> — trusted tools that help us run our website, forms, email, and scheduling, bound to protect your data.</li>
          <li><strong>Legal reasons</strong> — if required by law or to protect the safety of our community.</li>
        </ul>

        <h2>5. Email and text messages</h2>
        <p>
          We'll email or text you about your specific pickup. Marketing updates are optional — every
          newsletter has an unsubscribe link, and you can opt out of reminders any time by replying or
          contacting us.
        </p>

        <h2>6. Cookies and analytics</h2>
        <p>
          Our site may use basic cookies and analytics to understand how visitors use the site and to
          improve it. You can control cookies through your browser settings.
        </p>

        <h2>7. Data retention</h2>
        <p>
          We keep your information only as long as needed to provide our services, meet legal or tax
          requirements, and measure our impact — then we delete or anonymize it.
        </p>

        <h2>8. Security</h2>
        <p>
          We use reasonable safeguards to protect your information. No method of transmission over the
          internet is 100% secure, but we work to keep your data safe and limit access to those who need it.
        </p>

        <h2>9. Children's privacy</h2>
        <p>
          Our services are intended for adults booking pickups. We don't knowingly collect personal
          information from children under 13. If you believe a child has provided us information, contact us
          and we'll remove it.
        </p>

        <h2>10. Your choices and rights</h2>
        <p>
          You can ask us to access, correct, or delete your personal information, or to stop contacting you.
          Just email us and we'll take care of it.
        </p>

        <h2>11. Changes to this policy</h2>
        <p>We may update this policy as Everything Eco grows. We'll post the new version here with an updated date.</p>

        <h2>12. Contact us</h2>
        <p>Questions about your privacy? Reach out any time:</p>
        <ul>
          <li>Email: <a href="mailto:everythingeconyc@gmail.com">everythingeconyc@gmail.com</a></li>
          <li>Everything Eco NYC · Williamsburg, Brooklyn · New York City</li>
        </ul>

      </div>
    </div>
  </div>
);

export default PrivacyPage;
